<template>
  <el-dialog
    :visible.sync="reportViewerVisible"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    title=""
    size="large"
    top="5%"
    id="report-dialog">
    <el-row type="flex" align="middle" justify="end" style="padding-bottom: 24px;">
      <el-col :span="4">
        <el-row type="flex" align="middle" justify="start" id="report-viewer-title">
          Report Viewer
        </el-row>
      </el-col>
      <el-col :span="20">
        <el-row type="flex" align="middle" justify="end">
          <el-button
            v-if="reportType.base === 'audit' && getReportById(reportId) && !dontShowPostBtn.includes(getReportById(reportId).type) && userCanPost"
            type="success"
            @click="() => handlePostBtnClick()">
            Post Transactions
          </el-button>
          <el-button
            v-if="reportType.base === 'audit' && getReportById(reportId) && !dontShowPostBtn.includes(getReportById(reportId).type)"
            plain
            @click="generateAuditReport(swappedReportDetailType.toLowerCase())">
            Audit Report By {{ swappedReportDetailType }}
          </el-button>
          <el-button
            v-if="reportType.base === 'posting' && getReportById(reportId) && !dontShowPostBtn.includes(getReportById(reportId).type) && userCanPost"
            plain
            @click="generatePostingReport(swappedReportDetailType.toLowerCase())">
            Posting Report By {{ swappedReportDetailType }}
          </el-button>
          <el-button plain @click="printReport">
            Print
          </el-button>
          <el-button plain @click="closeViewer()">
            Close window
          </el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" align="middle" id="report-viewer">
      <iframe
        :src="reportId
          && getReportById(reportId) && getReportById(reportId).data
            ? `data:application/pdf;base64,${getReportById(reportId).data}`
            : ''"
        :height="reportHeight"
        width="100%"
        frameborder="0">
      </iframe>
    </el-row>
  </el-dialog>
</template>

<script>
  import printJS from 'print-js'
  import getTransactionReportParams from '@/utils/helpers/GetTransactionReportParams'
  import { notify } from '@/utils/helpers/Notify'
  import { mapGetters } from 'vuex'
  import { flattenArrayRecursive } from '@/utils/helpers/FlattenArray'
  import { buildUpdatePromiseIterable } from '@/utils/helpers/BuildPromiseIterable'
  import { setTemplate, getUnpostedResponses } from '@/utils/helpers/DataEntryViews/CreateReport'
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'

  export default {
    name: 'reportViewer',
    props: {
      reportViewerVisible: Boolean,
      reportId: String
    },
    data () {
      return {
        working: false,
        workingNotif: null,
        error: null,
        reportHeight: window.innerHeight - 200,
        dontShowPostBtn: [
          'posting',
          'costs',
          'ledger/detail',
          'ledger/summary',
          'checks/register'
        ]
      }
    },
    methods: {
      handlePostBtnClick (confirmed = false) {
        if (this.$store.getters.getUiSettings['CONFIRM_ON_POST'] && !confirmed) {
          this.showPostingConfirmationDialog()
        } else {
          this.generatePostingReport('transaction', true)
        }
      },
      /**
       * Shows a confirmation dialog when a user tries to post the transaction
       * @return {Promise<void>}
       */
      async showPostingConfirmationDialog () {
        try {
          await this.$confirm('Do you wish to post this transaction?', 'Warning', {
            confirmButtonText: 'Yes',
            cancelButtonText: 'Cancel',
            type: 'warning'
          })
          this.handlePostBtnClick(true)
        } catch (e) {
          console.log(e)
        }
      },
      /**
       * Determines whether the transactions used should be grabbed from this.filteredSelection or this.selection
       */
      getCurrentSelection () {
        return this.filteredSelection && this.filteredSelection.length ? this.filteredSelection : this.selection
      },
      /**
       * Sets properties of the data object required to cached parameters, depending on the type of report
       * @param   detailType    The specific type of report (account or transaction)
       */
      setCachedParamData (detailType) {
        try {
          let selection = this.getCurrentSelection()
          if (!(selection && Array.isArray(selection) && selection.length)) {
            throw new Error('No transactions selected')
          }
          if (detailType === 'account') {
            let currency = ''
            let data = ''
            if (selection[0].batch) {
              currency = this.getBankByBatchId(selection[0].batch).currency
              data = { transaction__ledger__id__in: selection.map(v => v.ledger) }
            } else if (selection[0].bank) {
              currency = this.getBankById(selection[0].bank).currency
              if (selection[0].ledger) {
                data = { transaction__ledger__id__in: selection.map(v => v.ledger) }
              } else {
                data = { transaction__ledger__id__in: selection.map(v => v.id) }
              }
            }
            if (currency) {
              data['base_currency'] = currency
            }
            return data
          } else {
            return getTransactionReportParams(this, this.getCurrentSelection())
          }
        } catch (e) {
          return e
        }
      },
      /**
       * Generates a report and sets transactions in this report to 'POSTED'
       * @param detailType
       * @param post
       */
      async generatePostingReport (detailType, post = false) {
        const endpoint = `posting/${detailType}`
        const loader = this.$loading({
          lock: true,
          fullscreen: true,
          text: post ? 'Posting transactions...' : 'Generating posting report...',
          customClass: 'trx-action-loader'
        })
        try {
          const data = this.setCachedParamData(detailType)
          this.closeViewer(true)
          if (post) {
            const statusUpdateResponses = await this.postTransactions()
            if (!Array.isArray(statusUpdateResponses) || getUnpostedResponses(statusUpdateResponses).length) return
          }
          let promises = [ this.$store.dispatch('cacheParams', { data }) ]
          let opts = this.$store.getters.getReportOptions(`reports/${endpoint}`)
          if (!opts) {
            promises.push(this.$store.dispatch('fetchFormFields', `reports/${endpoint}`))
          }
          this.working = true
          const resp = await Promise.all(promises)
          this.working = false
          const cachedParamsResponse = resp.find(v => v.id && v.hasOwnProperty('data'))
          if (!(cachedParamsResponse && cachedParamsResponse.id && cachedParamsResponse.reportId)) {
            throw new Error('Error caching parameters for Posting report')
          }
          const { id, reportId } = cachedParamsResponse
          const payload = setTemplate(this, endpoint, { query: { cached_parameters: id }, type: endpoint, reportId })
          return this.$store.dispatch('startRenderingReport', payload)
        } catch (e) {
          console.log(e)
          notify(this, 'error', e || 'Error generating posting report')
        } finally {
          this.working = false
          loader.close()
        }
      },
      /**
       * Generates a report and sets transactions in this report to 'POSTED'
       * @param detailType
       */
      async generateAuditReport (detailType) {
        const endpoint = `audit/${detailType}`
        const loader = this.$loading({
          lock: true,
          fullscreen: true,
          text: 'Generating audit report...',
          customClass: 'trx-action-loader'
        })
        try {
          const data = this.setCachedParamData(detailType)
          this.closeViewer(true)
          let promises = [ this.$store.dispatch('cacheParams', { data }) ]
          let opts = this.$store.getters.getReportOptions(`reports/${endpoint}`)
          if (!opts) {
            promises.push(this.$store.dispatch('fetchFormFields', `reports/${endpoint}`))
          }
          this.working = true
          const resp = await Promise.all(promises)
          this.working = false
          const cachedParamsResponse = resp.find(v => v.id && v.hasOwnProperty('data'))
          if (!(cachedParamsResponse && cachedParamsResponse.id && cachedParamsResponse.reportId)) {
            throw new Error(`Error caching parameters for Audit by ${capitalizeFirstLetter(detailType)} report`)
          }
          const { id, reportId } = cachedParamsResponse
          const payload = setTemplate(this, endpoint, { query: { cached_parameters: id }, type: endpoint, reportId })
          return this.$store.dispatch('startRenderingReport', payload)
        } catch (e) {
          console.log(e)
          notify(this, 'error', e || `Error generating Audit by ${capitalizeFirstLetter(detailType)} report`)
        } finally {
          this.working = false
          loader.close()
        }
      },
      /**
       * Closes the report viewer
       * @param preserveSelection
       */
      closeViewer (preserveSelection = false) {
        this.$emit('setVisibilityState', false)
        if (!preserveSelection) {
          this.$store.dispatch('updateTransactionSelection', [])
          this.$store.dispatch('updateFilteredTransactionSelection', [])
          this.$store.dispatch('resetReportState')
        }
      },
      /**
       * Marks selected transactions shown as either AUDITED or POSTED,
       * then removes said transactions from list after posting success
       * @param r
       * @param newStatus
       */
      markTransactionsShownAs (r, newStatus) {
        for (let i = 0; i < r.length; i++) {
          this.$store.dispatch('updateTransactionsShown', this.transactionsShown.map(v => {
            if (v && v.id === (r[i].id || r[i].body.id)) {
              v.status = newStatus
              v['newStatus'] = newStatus
            }
            return v
          }))
        }
      },
      /**
       * Handles posting the transactions
       * @return {Promise<never | T> | Promise.<T>}
       */
      async postTransactions () {
        try {
          let selection = this.getCurrentSelection()
          if (!(selection && selection.length)) {                                      // If the user hits the "Post Transactions" button from within the viewer, then 'selection' won't contain anything
            const report = this.getReportById(this.currentReportId)                    // In this case, you can get the transaction ids from the recently generated audit report, and get the actual transaction
            let t = flattenArrayRecursive(Object.values(report.transactions))          // objects from this.transactions, then use those to build the promise iterable
            selection = t.map(v => {
              const t = this.transactions.find(x => x.id === v)
              if (t) return t
            })
          }
          let promises = buildUpdatePromiseIterable(this.$store, selection, { status: 'POSTED' })
          const resp = await Promise.all(promises)
          if (!(resp && resp.length)) {
            throw new Error()
          }
          if (getUnpostedResponses(resp).length) return resp    // reason already shown by the 400 interceptor
          this.markTransactionsShownAs(resp, 'POSTED')
          // if (selection && selection.length && selection[0].transactionType === 'Accounts Payable') {
          //   this.$store.dispatch('changeModalVisibility', { entityType: 'remainderModal', visible: true })
          // }
          notify(this, 'success', 'Transactions Posted')
          return resp
        } catch (e) {
          console.log(e)
          notify(this, 'error', 'Error marking transactions as posted')
          return e
        }
      },
      /**
       * Prints the report
       */
      printReport () {
        const report = this.getReportById(this.reportId)
        if (report.pdfUrl) {
          printJS(report.pdfUrl)
        }
      }
    },
    computed: {
      ...mapGetters({
        getReports: 'getReports',
        getReportById: 'getReportById',
        reportErr: 'getReportError',
        selection: 'getSelectedTransactions',
        filteredSelection: 'getFilteredSelectedTransactions',
        reportViewStatus: 'getReportViewStatus',
        receivingReports: 'getReportFetchStatus',
        transactions: 'getTransactionsToBePosted',
        transactionsShown: 'getTransactionsShown',
        currentReportId: 'getCurrentReportId',
        postBtnVisible: 'getPostBtnVisibleStatus',
        getBankByBatchId: 'getBankByBatchId',
        getBankById: 'getBankById',
        getCurrencyById: 'getCurrencyById'
      }),
      /**
       * Returns an object that describes the current report type
       */
      reportType () {
        const currentReport = this.$store.getters.getReportById(this.reportId)
        let type = { base: null, detail: null }
        if (currentReport && currentReport.type && typeof currentReport.type === 'string') {
          type.base = currentReport.type.split('/')[0]
          type.detail = currentReport.type.split('/')[1] || null
        }
        return type
      },
      /**
       * Computed property that returns the counterpart to the current report type
       * @return {string}
       */
      swappedReportDetailType () {
        return this.reportType.detail === 'transaction' ? 'Account' : 'Transaction'
      },
      /**
       * Computed property that returns a boolean indicating whether the user is permitted to post a transaction
       * @return {*|boolean}
       */
      userCanPost () {
        if (this.$store.getters.checkIfUserHasRequiredPermissions(['auth.post_transactions'])) return true
        let selection = this.getCurrentSelection()
        if (!(selection && Array.isArray(selection) && selection.length)) {
          return false
        }
        const postPermissions = {
          'Accounts Payable': 'entry.post_accountspayable',
          'Journal Entry': 'entry.post_journal',
          'Payroll': 'entry.post_payrollentry',
          'Petty Cash': 'entry.post_pettycash'
        }
        let transactionTypes = [...new Set(this.selection.map(t => (t.source_code && t.source_code === 5) ? 'Payroll' : t.transactionType))]
        return this.$store.getters.checkIfUserHasAnyOfThePermissions(transactionTypes.map(t => postPermissions[t]))
      }
    },
    watch: {
      reportViewStatus () {
        let { selection } = this
        if (!this.currentReportId) return
        const { reportType } = this.reportViewStatus[this.currentReportId]
        let promises
        if (reportType.base === 'audit') {
          let filteredSelection = selection.filter(trx => {       // Since report is audit report, filter out transactions that aren't open or audited
            if (trx && trx.status) return ['OPEN', 'AUDITED'].includes(trx.status)
            return false
          })
          promises = buildUpdatePromiseIterable(this.$store, filteredSelection, { status: 'AUDITED' })
          Promise.all(promises)
            .then(r => {
              if (!r) return Promise.reject('No response')
              this.markTransactionsShownAs(r, 'AUDITED')
              return notify(this, 'success', 'Transactions Audited')
            })
            .catch(e => {
              console.log(e)
              return notify(this, 'error', 'Error marking transactions as audited')
            })
        }
      },
      working () {
        if (this.working) {
          this.workingNotif = this.$message({
            message: 'Preparing report options...',
            duration: 0
          })
        } else {
          this.workingNotif.close()
          this.workingNotif = null
        }
      }
    }
  }
</script>

<style scoped>
  #report-dialog {
    z-index: 11001 !important;
  }
  #report-viewer-title {
    font-size: 24px;
  }
</style>



// WEBPACK FOOTER //
// src/components/ReportViewer.vue